/**
 * Recently viewed row (sections/recently-viewed.liquid).
 *
 * Reads the product ids the theme keeps in localStorage (assets/recently-viewed-products.js: the theme records every
 * product page on load), drops the current product and shows the cards of the others. The row stays hidden until there is at
 * least one other product to show, so nothing is reserved on the page.
 *
 * Nothing is requested when the page loads: the row waits until it is within about 600px of the viewport. The cards come
 * from the shared request layer of assets/sold-out-alternatives.js: on a card page the ids already ride along with the
 * "same card" request (Other printings), so this row usually needs no request of its own; otherwise ONE search by id
 * (/search?q=id:1 OR id:2 ... through the Section Rendering API, the same way the theme's search drawer lists them) fetches
 * every missing card. A throttled or failed request is retried once; if it still fails the row stays hidden (no error).
 */
import { RecentlyViewed } from '@theme/recently-viewed-products';

const DEFAULT_MAX = 8;

/**
 * The nearest ancestor that scrolls vertically inside the page (this theme scrolls .page-wrapper, not the window), or null.
 * An IntersectionObserver only applies its rootMargin to its root, so it has to be told which element scrolls.
 * @param {Element} node
 * @returns {Element | null}
 */
function scrollParent(node) {
  for (let parent = node.parentElement; parent && parent !== document.body && parent !== document.documentElement; parent = parent.parentElement) {
    const overflow = getComputedStyle(parent).overflowY;
    if ((overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay') && parent.scrollHeight > parent.clientHeight + 1) return parent;
  }
  return null;
}

/**
 * Runs `start` once, when the row comes within about 600px of the viewport (an IntersectionObserver with a rootMargin).
 * The row is rendered hidden until it has something to show, and a hidden element is never "visible" to an
 * IntersectionObserver, so a 1px anchor (zero height in the layout) is put just before the hidden wrapper and observed
 * instead. Without IntersectionObserver (or with nothing that can be observed) the row starts when the browser is idle.
 * (Same function as in assets/sold-out-alternatives.js: this file must not load that one before the row is needed.)
 * @param {HTMLElement} element
 * @param {() => void} start
 * @returns {() => void} cancels the wait
 */
function whenNear(element, start) {
  let done = false;
  /** @type {IntersectionObserver | null} */
  let observer = null;
  /** @type {HTMLElement | null} */
  let anchor = null;

  /** @type {HTMLElement | null} */
  let hiddenRoot = null;
  for (let node = /** @type {HTMLElement | null} */ (element); node && node !== document.body; node = node.parentElement) {
    if (node.hidden) hiddenRoot = node;
  }
  let target = element;
  if (hiddenRoot) {
    anchor = document.createElement('div');
    anchor.setAttribute('aria-hidden', 'true');
    anchor.setAttribute('data-lazy-anchor', '');
    anchor.style.cssText = 'display:block;height:1px;margin:0 0 -1px;pointer-events:none';
    hiddenRoot.before(anchor);
    target = anchor;
  }

  const cleanup = () => {
    observer?.disconnect();
    observer = null;
    anchor?.remove();
    anchor = null;
  };
  const go = () => {
    if (done) return;
    done = true;
    cleanup();
    try {
      start();
    } catch (_) {
      // The row is optional: it stays hidden.
    }
  };

  if ('IntersectionObserver' in window && target.getClientRects().length > 0) {
    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) go();
      },
      { root: scrollParent(target), rootMargin: '600px 0px' }
    );
    observer.observe(target);
  } else if ('requestIdleCallback' in window) {
    window.requestIdleCallback(go, { timeout: 2500 });
  } else {
    window.setTimeout(go, 1500);
  }

  return () => {
    done = true;
    cleanup();
  };
}

class RecentlyViewedRow extends HTMLElement {
  /** @type {(() => void) | null} */
  #cancel = null;

  connectedCallback() {
    if (this.hasAttribute('data-started')) return;
    this.setAttribute('data-started', '');

    // The theme records the product page already; this only makes sure it also holds when that script did not run.
    const current = /^\d+$/.test(this.dataset.currentId || '') ? String(this.dataset.currentId) : '';
    if (current && !RecentlyViewed.getHistory().includes(current)) RecentlyViewed.addProduct(current);

    this.#cancel = whenNear(this, () => {
      this.#run().catch(() => {
        this.#show([]);
      });
    });
  }

  disconnectedCallback() {
    this.#cancel?.();
  }

  async #run() {
    const ds = this.dataset;
    const current = /^\d+$/.test(ds.currentId || '') ? String(ds.currentId) : '';
    if (!ds.helpers) {
      this.#show([]);
      return;
    }

    const helpers = await import(ds.helpers);
    const max = Math.min(12, Math.max(1, parseInt(ds.max || String(DEFAULT_MAX), 10) || DEFAULT_MAX));
    // Every id the card request carries is asked for, so a product that is gone (or not searchable) does not leave a gap.
    const ids = helpers.viewedIds(current);
    if (!ids.length) {
      this.#show([]);
      return;
    }

    // The product on the page (when there is one): its card request carries these ids, so it is shared with Other printings.
    const me = ds.title
      ? helpers.identityOf({ id: current, title: ds.title, game: ds.game, kind: ds.kind, lang: ds.lang, vendor: ds.vendor, number: ds.number })
      : null;
    const byId = await helpers.viewedItems(ds.source || 'sold-out-alternatives', me, ids);
    const ordered = ids.map((id) => byId.get(id)).filter(Boolean).slice(0, max);

    this.#show(ordered.map((/** @type {any} */ item) => helpers.buildCard(item)));
  }

  /** @param {HTMLLIElement[]} cards */
  #show(cards) {
    const list = this.querySelector('[data-rv-list]');
    const note = this.querySelector('[data-rv-note]');
    const wrap = this.closest('[data-rv-wrap]');
    if (!list) return;

    if (!cards.length) {
      if (this.dataset.design === 'true' && note instanceof HTMLElement) note.hidden = false;
      return;
    }

    list.replaceChildren(...cards);
    if (note instanceof HTMLElement) note.hidden = true;
    if (wrap instanceof HTMLElement) wrap.hidden = false;
    this.#bindControls(wrap);
  }

  /** @param {Element | null} wrap */
  #bindControls(wrap) {
    const viewport = this.querySelector('[data-rv-viewport]');
    const prev = this.querySelector('[data-rv-prev]');
    const next = this.querySelector('[data-rv-next]');
    const clear = this.querySelector('[data-rv-clear]');

    if (clear instanceof HTMLElement) {
      clear.addEventListener('click', () => {
        RecentlyViewed.clearProducts();
        if (wrap instanceof HTMLElement) wrap.hidden = true;
      });
    }

    if (!(viewport instanceof HTMLElement) || !(prev instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) return;

    const update = () => {
      const overflowing = viewport.scrollWidth > viewport.clientWidth + 1;
      prev.hidden = !overflowing;
      next.hidden = !overflowing;
      prev.disabled = viewport.scrollLeft <= 1;
      next.disabled = viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 1;
    };

    /** @param {number} direction */
    const scroll = (direction) => {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      viewport.scrollBy({ left: direction * Math.max(160, viewport.clientWidth * 0.85), behavior: reduced ? 'auto' : 'smooth' });
    };

    prev.addEventListener('click', () => scroll(-1));
    next.addEventListener('click', () => scroll(1));
    viewport.addEventListener('scroll', update, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(viewport);
    requestAnimationFrame(update);
  }
}

if (!customElements.get('recently-viewed-row')) customElements.define('recently-viewed-row', RecentlyViewedRow);
